/**
 * Trustpilot Reviews "load more".
 *
 * Each instance fetches the next page of pre-rendered review cards from the
 * `growtherapy/v1/trustpilot-reviews` REST endpoint and appends them to the grid.
 * The server renders the card markup, so this module only moves HTML into the DOM —
 * there is no client-side templating to keep in sync with the PHP renderer.
 */

import { initCollapsibleContent } from '../collapsible-content/index.js';

/**
 * Wire up a single Trustpilot reviews block.
 *
 * @param {HTMLElement} root The `.gt-trustpilot-reviews` element.
 */
function initLoadMore(root) {
	const grid = root.querySelector('[data-trustpilot-reviews-grid]');
	const button = root.querySelector('[data-trustpilot-reviews-more-cta]');
	const endpoint = root.dataset.endpoint;
	const perPage = root.dataset.perPage || '12';

	if (!grid || !button || !endpoint) {
		return;
	}

	const loader = root.querySelector('[data-trustpilot-reviews-loading]');
	let nextPage = parseInt(root.dataset.nextPage || '2', 10);
	let isLoading = false;

	button.addEventListener('click', async () => {
		if (isLoading) {
			return;
		}

		isLoading = true;
		button.disabled = true;
		button.setAttribute('aria-busy', 'true');

		// Reveal the loading animation only for the duration of the request.
		if (loader) {
			loader.hidden = false;
		}

		const url = new URL(endpoint);
		url.searchParams.set('page', String(nextPage));
		url.searchParams.set('per_page', String(perPage));

		try {
			const response = await fetch(url.toString(), {
				headers: { Accept: 'application/json' },
			});

			if (!response.ok) {
				throw new Error(`Request failed: ${response.status}`);
			}

			const data = await response.json();

			if (data.html) {
				const template = document.createElement('template');
				template.innerHTML = data.html.trim();
				const firstNew = template.content.firstElementChild;

				// Capture the collapsible elements before appending, because
				// moving the fragment into the DOM empties `template.content`.
				const collapsibles = Array.from(
					template.content.querySelectorAll('[data-collapsible-content]'),
				);

				grid.append(template.content);

				// Wire up "View more"/"View less" on the freshly added cards.
				// This must run after the append so the clamp measurement
				// (scrollHeight vs clientHeight) reflects the rendered layout.
				collapsibles.forEach(initCollapsibleContent);

				// Move focus to the first newly added card so screen reader and
				// keyboard users land on the fresh content.
				if (firstNew) {
					firstNew.setAttribute('tabindex', '-1');
					firstNew.focus({ preventScroll: true });
				}
			}

			nextPage += 1;

			if (!data.has_more) {
				button.remove();
				return;
			}
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error('Trustpilot reviews: unable to load more reviews.', error);
		} finally {
			isLoading = false;

			// Always hide the loader, including the early-return path where the
			// button is removed because there are no more reviews to fetch.
			if (loader) {
				loader.hidden = true;
			}
		}

		button.removeAttribute('aria-busy');
		button.disabled = false;
	});
}

document.addEventListener('DOMContentLoaded', () => {
	document.querySelectorAll('[data-trustpilot-reviews]').forEach(initLoadMore);
});
